// Pure parts of the report date range (Stephanie 2026-10-16) — no React, no
// app imports, so the offline gate can run them.

export interface DateRange {
  from: string;
  to: string;
}

export type RangePreset =
  | "last-7"
  | "last-30"
  | "mtd"
  | "last-month"
  | "qtd"
  | "ytd"
  | "custom";

export const PRESET_LABELS: Record<Exclude<RangePreset, "custom">, string> = {
  "last-7": "Last 7 days",
  "last-30": "Last 30 days",
  "mtd": "Month to date",
  "last-month": "Last month",
  "qtd": "Quarter to date",
  "ytd": "Year to date",
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Pure: a custom From/To the server will accept (dates, From not after To). */
export function validCustomRange(from: string, to: string): boolean {
  return ISO_DATE.test(from) && ISO_DATE.test(to) && from <= to;
}

function fmt(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function presetRange(p: Exclude<RangePreset, "custom">): DateRange {
  const today = new Date();
  const to = fmt(today);
  switch (p) {
    case "last-7": {
      const from = new Date(today);
      from.setDate(from.getDate() - 7);
      return { from: fmt(from), to };
    }
    case "last-30": {
      const from = new Date(today);
      from.setDate(from.getDate() - 30);
      return { from: fmt(from), to };
    }
    case "mtd": {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: fmt(from), to };
    }
    case "last-month": {
      const fromD = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const toD = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: fmt(fromD), to: fmt(toD) };
    }
    case "qtd": {
      const quarter = Math.floor(today.getMonth() / 3);
      const from = new Date(today.getFullYear(), quarter * 3, 1);
      return { from: fmt(from), to };
    }
    case "ytd":
      return { from: fmt(new Date(today.getFullYear(), 0, 1)), to };
  }
}

/** Pure: the range + rep the address bar asks for (bad values fall back). */
export function readReportParams(
  params: URLSearchParams,
  initial: Exclude<RangePreset, "custom">,
): { preset: RangePreset; range: DateRange; repId: string } {
  const raw = params.get("range");
  const repId = params.get("rep") ?? "";
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  if (raw === "custom" && validCustomRange(from, to)) return { preset: "custom", range: { from, to }, repId };
  const preset = raw !== null && raw in PRESET_LABELS ? (raw as Exclude<RangePreset, "custom">) : initial;
  return { preset, range: presetRange(preset), repId };
}

